import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { AddButton, Field, LiveScreen, QueryState, Select, StatusPill, formatDateTime, type Column } from '@/components/live';
import { DataGrid } from '@/components/DataGrid';
import { StatCards, applyStat } from '@/components/StatCards';
import { RecordDrawer } from '@/components/RecordDrawer';
import { useRows } from '@/hooks/useResource';
import { callRpc } from '@/api/resources';
import { useAuth } from '@/auth/AuthProvider';
import { toast } from '@/lib/toast';

interface HsnRow { code: string; description: string; category: string; gst_rate_pct: number | null; notes: string | null; is_active: boolean; updated_at: string }
type Row = HsnRow & { id: string };

const CATEGORIES = [
  { id: 'timber', label: 'Timber' }, { id: 'fuelwood', label: 'Fuelwood & charcoal' }, { id: 'ntfp', label: 'NTFP' },
  { id: 'fruit', label: 'Fruit & nuts' }, { id: 'planting_material', label: 'Planting material' }, { id: 'other', label: 'Other' },
];
const EMPTY = { code: '', description: '', category: 'timber', gst_rate_pct: '', notes: '', is_active: true };

/**
 * The HSN codes every sale names its goods by (owner, 2026-09-27: "create HSN Database and connect
 * (as required for all parties)"). One catalogue for the application, like the hectare factors:
 * everyone reads it, the app owner keeps it. GST rates were not seeded - enter them from the
 * current rate schedule.
 */
export default function HsnCodesScreen() {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const appOwner = profile?.tier === 0;
  const [stat, setStat] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Row | 'new' | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const codes = useRows<HsnRow>('hsn_codes', {
    search: search ? { columns: ['code', 'description'], term: search } : undefined,
    orderBy: { column: 'code' },
  });
  const rows = useMemo(() => (codes.data ?? []).map((row) => ({ ...row, id: row.code })), [codes.data]);
  const STATS = useMemo(() => [
    { key: 'all', label: 'HSN codes', tone: 'neutral' as const, match: () => true },
    ...CATEGORIES.slice(0, 5).map((category) => ({ key: category.id, label: category.label, tone: 'info' as const, match: (row: Row) => row.category === category.id })),
    { key: 'no_rate', label: 'No GST rate yet', tone: 'warn' as const, match: (row: Row) => row.gst_rate_pct == null },
  ], []);
  const visible = useMemo(() => applyStat(rows, STATS, stat), [rows, STATS, stat]);

  function edit(row: Row | 'new') {
    setForm(row === 'new' ? EMPTY : { code: row.code, description: row.description, category: row.category,
      gst_rate_pct: row.gst_rate_pct == null ? '' : String(row.gst_rate_pct), notes: row.notes ?? '', is_active: row.is_active });
    setOpen(row);
  }
  async function save() {
    if (!/^[0-9]{4}([0-9]{2}){0,2}$/.test(form.code.trim())) { toast.error('An HSN code is 4, 6 or 8 digits.'); return; }
    if (!form.description.trim()) { toast.error('Give the description.'); return; }
    setSaving(true);
    try {
      await callRpc('fn_save_hsn_code', {
        p_code: form.code.trim(), p_description: form.description.trim(), p_category: form.category,
        p_gst_rate_pct: form.gst_rate_pct.trim() === '' ? null : Number(form.gst_rate_pct), p_notes: form.notes.trim() || null, p_is_active: form.is_active,
      });
      await queryClient.invalidateQueries({ queryKey: ['hsn_codes'] });
      toast.success(`${form.code.trim()} saved.`);
      setOpen(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  }

  const columns: Array<Column<Row>> = [
    { key: 'code', label: 'HSN', render: (row) => <strong>{row.code}</strong> },
    { key: 'description', label: 'Description', render: (row) => row.description },
    { key: 'category', label: 'Category', render: (row) => CATEGORIES.find((entry) => entry.id === row.category)?.label ?? row.category },
    { key: 'gst_rate_pct', label: 'GST %', align: 'right', render: (row) => (row.gst_rate_pct == null ? <span className="muted">not set</span> : `${row.gst_rate_pct}%`) },
    { key: 'is_active', label: 'Status', render: (row) => <StatusPill value={row.is_active ? 'active' : 'inactive'} /> },
    { key: 'updated_at', label: 'Updated', render: (row) => formatDateTime(row.updated_at) },
  ];
  const editing = open && open !== 'new' ? open : null;

  return (
    <LiveScreen
      actions={appOwner ? <AddButton label="Add HSN code" onClick={() => edit('new')} /> : undefined}
      subtitle="The HSN codes sales name their goods by: one catalogue for every organisation, kept by the app owner."
      title="HSN codes"
    >
      <StatCards active={stat} onSelect={setStat} rows={rows} stats={STATS} />
      <QueryState empty="No HSN code on record." query={codes}>
        <DataGrid columns={columns} exportName="hsn-codes" onRowClick={edit} onSearchChange={setSearch} recordNoun="HSN code"
                  rows={visible} screenKey="org.settings" search={search} searchPlaceholder="Code or description" selectedId={editing?.id} />
      </QueryState>
      <RecordDrawer
        footer={<>
          <button className="cancel" onClick={() => setOpen(null)} type="button">{appOwner ? 'Cancel' : 'Close'}</button>
          {appOwner && <button className="primary" disabled={saving} onClick={() => void save()} type="button">{saving ? 'Saving...' : 'Save'}</button>}
        </>}
        onClose={() => setOpen(null)} open={open !== null} record={null}
        subtitle={appOwner ? 'Changes apply to every organisation.' : 'Kept by the app owner.'}
        title={open === 'new' ? 'New HSN code' : `HSN ${editing?.code ?? ''}`}
      >
        <fieldset className="spec-fieldset" disabled={!appOwner}>
          <div className="field-row">
            <Field label="HSN code" required>
              <input disabled={open !== 'new'} inputMode="numeric" onChange={(event) => setForm({ ...form, code: event.target.value.replace(/\D/g, '').slice(0, 8) })}
                     placeholder="e.g. 440349" value={form.code} />
            </Field>
            <Field label="Category" required><Select onChange={(value) => setForm({ ...form, category: value })} options={CATEGORIES} value={form.category} /></Field>
          </div>
          <Field label="Description" required>
            <textarea onChange={(event) => setForm({ ...form, description: event.target.value })} rows={2} value={form.description} />
          </Field>
          <div className="field-row">
            <Field hint="From the current GST rate schedule." label="GST rate (%)">
              <input inputMode="decimal" onChange={(event) => setForm({ ...form, gst_rate_pct: event.target.value })} value={form.gst_rate_pct} />
            </Field>
            <Field label="In use">
              <label className="check-inline"><input checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} type="checkbox" /> Offered on sales</label>
            </Field>
          </div>
          <Field label="Notes"><textarea onChange={(event) => setForm({ ...form, notes: event.target.value })} rows={2} value={form.notes} /></Field>
        </fieldset>
      </RecordDrawer>
    </LiveScreen>
  );
}
